import { Heart, Bed, Bath, Ruler, MapPin, ExternalLink, Trash2 } from "lucide-react";
import { RENTAL } from "@/constants/testIds";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1627141440602-e9c1e5fd2fbf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmFsJTIwaG91c2UlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODAwOTkyMzV8MA&ixlib=rb-4.1.0&q=85";

export const RentalCard = ({ rental, onOpen, onFav, onDelete, index = 0 }) => {
  const rentStr =
    rental.rent != null ? `$${Math.round(rental.rent).toLocaleString()}` : "—";

  const badges = [];
  if (rental.garage) badges.push("Garage");
  if (rental.parking && !rental.garage) badges.push("Parking");
  if (rental.pet_friendly) badges.push("Pets OK");
  if (rental.furnished) badges.push("Furnished");
  if (rental.laundry === "in-unit") badges.push("In-unit Laundry");

  return (
    <article
      data-testid={RENTAL.rentalCard(rental.id)}
      className="group bg-white rounded-3xl border border-stone-200 overflow-hidden card-lift fade-up flex flex-col"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <button
        type="button"
        onClick={() => onOpen?.(rental)}
        data-testid={RENTAL.rentalOpen(rental.id)}
        className="text-left relative block aspect-[4/3] w-full overflow-hidden bg-stone-100"
      >
        <img
          src={rental.image_url || FALLBACK_IMG}
          alt={rental.title}
          onError={(e) => {
            e.currentTarget.src = FALLBACK_IMG;
          }}
          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
        />
        <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap max-w-[70%]">
          {badges.slice(0, 2).map((b) => (
            <span
              key={b}
              className="bg-white/85 backdrop-blur-sm rounded-full px-2.5 py-1 text-[10px] font-medium text-stone-700 tracking-wide"
            >
              {b}
            </span>
          ))}
        </div>
        <div className="absolute top-3 right-3 flex gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onFav?.(rental);
            }}
            data-testid={RENTAL.rentalFav(rental.id)}
            className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-sm"
            aria-label="Favorite"
          >
            <Heart
              size={16}
              strokeWidth={1.8}
              className={
                rental.favorite
                  ? "text-[#C25934] fill-[#C25934]"
                  : "text-stone-600"
              }
            />
          </button>
        </div>
      </button>

      <div className="p-5 flex flex-col gap-2 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-heading text-3xl font-medium text-stone-900 leading-none">
            {rentStr}
            <span className="text-base text-stone-400 font-body ml-1">/mo</span>
          </span>
          {rental.source_url && (
            <a
              href={rental.source_url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-stone-400 hover:text-[#4A5D4E] transition-colors"
              aria-label="Open source"
            >
              <ExternalLink size={15} strokeWidth={1.8} />
            </a>
          )}
        </div>

        <h3 className="font-body text-base text-stone-700 line-clamp-1 mt-0.5">
          {rental.title || "Untitled rental"}
        </h3>

        {(rental.city || rental.address) && (
          <div className="flex items-center gap-1.5 text-xs text-stone-500">
            <MapPin size={12} strokeWidth={1.6} />
            <span className="truncate">
              {rental.address ? `${rental.address}` : rental.city}
            </span>
          </div>
        )}

        <div className="flex items-center gap-4 mt-2 text-xs text-stone-600 font-medium tracking-wider uppercase">
          <Meta icon={<Bed size={13} strokeWidth={1.7} />} value={fmt(rental.bedrooms, "bd")} />
          <Meta icon={<Bath size={13} strokeWidth={1.7} />} value={fmt(rental.bathrooms, "ba")} />
          {rental.sqft != null && (
            <Meta
              icon={<Ruler size={13} strokeWidth={1.7} />}
              value={`${Math.round(rental.sqft)} sf`}
            />
          )}
        </div>

        <div className="mt-auto pt-3 flex items-center justify-between">
          <button
            onClick={() => onOpen?.(rental)}
            className="text-sm font-medium text-[#4A5D4E] hover:text-[#3D4D40] underline-offset-4 hover:underline"
          >
            View details
          </button>
          <button
            type="button"
            onClick={() => onDelete?.(rental)}
            data-testid={RENTAL.rentalDelete(rental.id)}
            className="text-stone-300 hover:text-[#C25934] transition-colors"
            aria-label="Delete"
          >
            <Trash2 size={16} strokeWidth={1.7} />
          </button>
        </div>
      </div>
    </article>
  );
};

const Meta = ({ icon, value }) => (
  <span className="inline-flex items-center gap-1">
    {icon}
    {value}
  </span>
);

const fmt = (n, suffix) => (n == null ? `— ${suffix}` : `${n} ${suffix}`);

export default RentalCard;
